import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  FileText,
  Info,
  List,
  Scale,
  Shield,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WizardLayout } from "@/components/layout/wizard-layout";
import { EvaluationLoadingScreen } from "./evaluation-loading-screen";
import { LOADING_DURATIONS } from "@/config/loadingDurations";
import { ImpactResultsTable } from "@/components/impact-analysis/ImpactResultsTable";
import { ClassificationReferenceTable } from "@/components/impact-analysis/ClassificationReferenceTable";
import { Badge } from "@/components/ui/badge";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import { Button } from "@/components/ui/button";
import { PaywallButton } from "@/components/ui/PaywallButton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MIN_LOADING_DURATION_MS, NIS2_EVALUATION_STEPS } from "@/constants";
import { useToast } from "@/hooks/use-toast";
import { useUpdateEntityData } from "@/hooks/api/mutate/useUpdateEntityImpactAnalysis";
import type { ClassificationType, EntityData } from "@/types/entity";
import {
  type ClassificationConditions,
  type ClassificationResult,
  type CompanyData,
  classifyNIS2Entity,
  type SectorGroups,
} from "@/utils/nis2-classifier";
import { RESULT_ENUM } from "../../utils/nis2-classifier-v2";
import type { SelectedService } from "@/types/service";
import { useSessionSwitcher } from "../../contexts/SessionSwitcherContext";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import { CREDIT_ACTION_TYPES } from "@/constants/credit";
import { PaywallDialog } from "@/components/PaywallDialog";

interface Step3NISClassificationProps {
  entityData: EntityData;
  selectedServices: SelectedService[];
  sectorClassification: any;
  onNext: (classification: ClassificationResult) => void;
  onBack: () => void;
  onStepClick?: (stepIndex: number) => void;
  currentStep?: number;
  steps?: string[];
  userAvatar?: React.ReactNode;
}

export function Step3NISClassification({
  entityData,
  selectedServices,
  sectorClassification,
  onNext,
  onBack,
  onStepClick,
  currentStep = 3,
  steps = [],
  userAvatar,
}: Step3NISClassificationProps) {
  // Use the selectedServices prop directly
  const { toast } = useToast();
  const { mutate: updateEntityData } = useUpdateEntityData();
  const navigate = useNavigate();
  const [classification, setClassification] =
    useState<ClassificationResult | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [conditionsOpen, setConditionsOpen] = useState(false);

  const { currentSessionId } = useSessionSwitcher();

  // Credit gate for analiza_efect_perturbator (small + essential entities only)
  const isSmallEssential =
    entityData.dimensiune === "MICĂ" &&
    entityData.nis_classification?.classification === "ESSENTIAL";
  const { hasInsufficientCredits: hasInsufficientEpCredits } =
    useCheckActionCredits(
      {
        session_id: currentSessionId || "",
        action_type: CREDIT_ACTION_TYPES.ANALIZA_EFECT_PERTURBATOR,
      },
      !!currentSessionId && isSmallEssential
    );
  const hasInsufficientCredits = isSmallEssential && hasInsufficientEpCredits;
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    if (entityData.nis_classification) {
      setClassification(entityData.nis_classification);
      setIsEvaluating(false);

      startEvaluation(false);
      return;
    }

    startEvaluation(true);
  }, []);

  const startEvaluation = async (showLoading = true) => {
    if (showLoading) {
      setIsEvaluating(true);
    }

    const minDelay = showLoading
      ? new Promise((resolve) =>
        setTimeout(resolve, LOADING_DURATIONS.nis2Start)
      )
      : Promise.resolve();

    // Prepare data for classification
    const companyData: CompanyData = {
      companySize:
        entityData.dimensiune === "MICĂ"
          ? "Mică"
          : entityData.dimensiune === "MIJLOCIE"
            ? "Mijlocie"
            : "Mare",

      establishedInRomania:
        entityData.relatie_stat_roman?.stabilita_in_romania ?? true,
      isNationalCritical:
        entityData.relatie_stat_roman?.entitate_critica_294_2024 ?? false,
      services: selectedServices.map((s) => s.entity_id),

      providesServicesInRomania:
        entityData.situatie_servicii_ue?.furnizeaza_servicii_romania ?? true,
      headquarteredInRomania:
        entityData.relatie_stat_roman?.sediu_principal_in_romania ?? true,
      isPublicAdminEntityRomania:
        entityData.relatie_stat_roman?.entitate_administratie_publica ?? false,
      providesServicesInEuMemberState:
        entityData.situatie_servicii_ue?.furnizeaza_servicii_ue ?? false,
      isSoleProvider: false, // Would be determined from risk assessment
      publicImpactLevel: null, // Would come from impact analysis
      systemicRiskLevel: null,
      isAlreadyCriticalEntity: false,
      sectors: {
        isDNSProvider:
          sectorClassification?.absolutePriority?.isDNSProvider || false,
        isTLDRegistry:
          sectorClassification?.absolutePriority?.isTLDRegistry || false,
        isQualifiedTrustProvider:
          sectorClassification?.absolutePriority?.isQualifiedTrustProvider ||
          false,
        isCentralPublicAdmin:
          sectorClassification?.absolutePriority?.isCentralPublicAdmin || false,
        energySector: sectorClassification?.sectors?.energySector || [],
        transportSector: sectorClassification?.sectors?.transportSector || [],
        bankingSector: sectorClassification?.sectors?.bankingSector || [],
        financialMarkets: sectorClassification?.sectors?.financialMarkets || [],
        healthSector: sectorClassification?.sectors?.healthSector || [],
        waterSector: sectorClassification?.sectors?.waterSector || [],
        digitalInfraOther:
          sectorClassification?.sectors?.digitalInfraOther || [],
        ictServiceManagement:
          sectorClassification?.sectors?.ictServiceManagement || [],
        foodSector: sectorClassification?.sectors?.foodSector || [],
        searchEngines: sectorClassification?.sectors?.searchEngines || [],
        isPublicNetworkProvider:
          sectorClassification?.digitalInfra?.isPublicNetworkProvider || false,
        isPublicCommServiceProvider:
          sectorClassification?.digitalInfra?.isPublicCommServiceProvider ||
          false,
        isManagedSecurityProvider:
          sectorClassification?.digitalInfra?.isManagedSecurityProvider ||
          false,
        publicAdminServices:
          sectorClassification?.sectors?.publicAdminServices || [],
      },
      analizaImpact: entityData.impact_analysis,
    };

    const result = classifyNIS2Entity(companyData);
    setClassification(result);

    await minDelay;

    const hasChanged =
      entityData.nis_classification?.classificationV2 !==
      result.classificationV2;

    if (!hasChanged) {
      if (showLoading) {
        setIsEvaluating(false);
      }
      return;
    }

    // Save classification to database
    updateEntityData(
      {
        session_id: currentSessionId,
        updates: {
          nis_classification: result,
        },
      },
      {
        onSuccess: () => {
          if (showLoading) {
            setIsEvaluating(false);
          }
        },
        onError: (error) => {
          console.error("Failed to save classification:", error);
          toast({
            title: "Eroare la salvare",
            description:
              "Clasificarea nu a putut fi salvată. Vă rugăm încercați din nou.",
            variant: "destructive",
          });
          if (showLoading) {
            setIsEvaluating(false);
          }
        },
      }
    );
  };

  const getClassificationIcon = (classification: string) => {
    switch (classification) {
      case "ESSENTIAL":
        return <Shield className="h-5 w-5" />;
      case "IMPORTANT":
        return <AlertTriangle className="h-5 w-5" />;
      default:
        return <Info className="h-5 w-5" />;
    }
  };

  const renderServicesAnalysis = () => {
    const annexa1Services = selectedServices.filter(
      (s) => s.category_id === "1"
    );
    const annexa2Services = selectedServices.filter(
      (s) => s.category_id === "2"
    );

    return (
      <Card className="border-accent/20">
        <Collapsible open={servicesOpen} onOpenChange={setServicesOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-2 cursor-pointer hover:bg-accent/5">
              <CardTitle className="text-base md:text-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <List className="h-5 w-5 text-accent" />
                  Servicii Analizate ({selectedServices.length})
                </div>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                />
              </CardTitle>
              <CardDescription className="text-xs md:text-sm">
                Servicii identificate din codul CAEN și selecțiile anterioare
              </CardDescription>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 md:pt-0 pb-4 md:pb-4">
              <div className="space-y-5 mt-2">
                {annexa1Services.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive" className="text-xs">
                        Anexa 1
                      </Badge>
                      <span className="text-sm md:text-base font-medium">
                        Servicii Esențiale
                      </span>
                    </div>
                    <div className="">
                      <Table className="border border-ds-border-200">
                        <TableHeader>
                          <TableRow className="bg-ds-surface-100">
                            <TableHead className="border border-ds-border-200 text-xs md:text-sm">
                              Sector
                            </TableHead>
                            <TableHead className="border border-ds-border-200 text-xs md:text-sm">
                              Subsector
                            </TableHead>
                            <TableHead className="border border-ds-border-200 text-xs md:text-sm">
                              Serviciu
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {annexa1Services.map((service, index) => (
                            <TableRow
                              key={service.entity_id}
                              className={
                                index % 2 === 0
                                  ? "bg-white"
                                  : "bg-ds-surface-50"
                              }
                            >
                              <TableCell className="border border-ds-border-200 text-xs md:text-sm">
                                {service.sector_name}
                              </TableCell>
                              <TableCell className="border border-ds-border-200 text-xs md:text-sm">
                                {service.subsector_name || "N/A"}
                              </TableCell>
                              <TableCell className="border border-ds-border-200 text-xs md:text-sm font-medium">
                                {service.name}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {annexa2Services.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        Anexa 2
                      </Badge>
                      <span className="text-sm md:text-base font-medium">
                        Servicii Importante
                      </span>
                    </div>
                    <div className="">
                      <Table className="border border-ds-border-200">
                        <TableHeader>
                          <TableRow className="bg-ds-surface-100">
                            <TableHead className="border border-ds-border-200 text-xs md:text-sm">
                              Sector
                            </TableHead>
                            <TableHead className="border border-ds-border-200 text-xs md:text-sm">
                              Subsector
                            </TableHead>
                            <TableHead className="border border-ds-border-200 text-xs md:text-sm">
                              Serviciu
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {annexa2Services.map((service, index) => (
                            <TableRow
                              key={service.entity_id}
                              className={
                                index % 2 === 0
                                  ? "bg-white"
                                  : "bg-ds-surface-50"
                              }
                            >
                              <TableCell className="border border-ds-border-200 text-xs md:text-sm">
                                {service.sector_name}
                              </TableCell>
                              <TableCell className="border border-ds-border-200 text-xs md:text-sm">
                                {service.subsector_name || "N/A"}
                              </TableCell>
                              <TableCell className="border border-ds-border-200 text-xs md:text-sm font-medium">
                                {service.name}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  };

  const renderPillarAnalysis = (sectorGroups: SectorGroups) => (
    <div className="grid gap-3 md:grid-cols-3">
      <Card className="border-primary/20">
        <CardHeader className="pb-0 md:pb-2">
          <CardTitle className="text-sm md:text-base flex items-center gap-2">
            <Scale className="h-4 w-4 text-primary" />
            Dimensiunea Entității
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-3 md:pt-0 md:pb-3">
          <Badge
            variant="outline"
            className="bg-primary/10 text-primary border-primary/30 mb-1 text-xs md:text-sm"
          >
            {entityData.dimensiune}
          </Badge>
          <p className="text-xs md:text-sm text-muted-foreground">
            Clasificare conform pragurilor EU SME
          </p>
        </CardContent>
      </Card>

      <Card className="border-accent/20">
        <CardHeader className="pb-0 md:pb-2">
          <CardTitle className="text-sm md:text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-accent" />
            Sectorul de Activitate
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-3 md:pt-0 md:pb-3">
          <div className="flex flex-wrap gap-1 mb-1">
            {sectorGroups.C2 && (
              <Badge variant="destructive" className="text-xs">
                C2 - Critică
              </Badge>
            )}
            {sectorGroups.C3 && (
              <Badge variant="secondary" className="text-xs">
                C3 - Principală
              </Badge>
            )}
            {sectorGroups.C4 && (
              <Badge variant="outline" className="text-xs">
                C4 - Digitală
              </Badge>
            )}
            {sectorGroups.C7 && (
              <Badge variant="secondary" className="text-xs">
                C7 - Publică
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Mapare conform Anexelor 1 și 2
          </p>
        </CardContent>
      </Card>

      <Card className="border-warning/20">
        <CardHeader className="pb-0 md:pb-2">
          <CardTitle className="text-sm md:text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warning" />
            Criterii Excepționale
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-3 md:pt-0 md:pb-3">
          <Badge
            variant={sectorGroups.C2 ? "destructive" : "outline"}
            className="text-xs mb-1 text-xs md:text-sm"
          >
            {sectorGroups.C2 ? "Prioritate Absolută" : "Fără Prioritate"}
          </Badge>
          <p className="text-xs md:text-sm text-muted-foreground">
            Reguli speciale și Articolul 9
          </p>
        </CardContent>
      </Card>
    </div>
  );

  const renderDetailedConditionsAnalysis = (
    conditions: ClassificationConditions
  ) => {
    const companyThresholds = {
      employees: {
        threshold: entityData.dimensiune === "MARE" ? 250 : 50,
        actual: entityData.nr_salariati,
      },
      turnover: {
        threshold: entityData.dimensiune === "MARE" ? 50000000 : 10000000,
        actual: entityData.cifra_afaceri,
      },
      assets: {
        threshold: entityData.dimensiune === "MARE" ? 43000000 : 10000000,
        actual: entityData.active,
      },
    };

    const dataWithListaSpeciala = entityData.selected_services.filter(
      (service) => service.lista_oficiala || service.smart_search
    );
    const hasListeSpeciale = dataWithListaSpeciala.length > 0;

    return (
      <Card className="border-primary/20">
        <Collapsible open={conditionsOpen} onOpenChange={setConditionsOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-0 md:pb-2 cursor-pointer hover:bg-primary/5">
              <CardTitle className="text-sm md:text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Analiză Detaliată a Condițiilor
                </div>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${conditionsOpen ? "rotate-180" : ""}`}
                />
              </CardTitle>
              <CardDescription className="text-xs md:text-sm pb-2 md:pb-0">
                Detalii complete despre evaluarea criteriilor de clasificare
              </CardDescription>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 pb-4 space-y-4 md:pt-0 md:pb-4">
              {/* Company Size Analysis */}
              <div className="space-y-2">
                <h4 className="text-sm font-heading font-semibold flex items-center gap-1">
                  <Scale className="h-3 w-3" />
                  Analiza Dimensiune Entitate
                </h4>
                <div className="space-y-1 ml-4">
                  <div className="flex items-center justify-between text-xs">
                    <span>Nr. salariați:</span>
                    <span
                      className={
                        companyThresholds.employees.actual >=
                          companyThresholds.employees.threshold
                          ? "text-destructive font-medium"
                          : "text-muted-foreground"
                      }
                    >
                      {companyThresholds.employees.actual.toLocaleString()} /{" "}
                      {companyThresholds.employees.threshold.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span>Cifră de afaceri:</span>
                    <span
                      className={
                        companyThresholds.turnover.actual >=
                          companyThresholds.turnover.threshold
                          ? "text-destructive font-medium"
                          : "text-muted-foreground"
                      }
                    >
                      €
                      {(companyThresholds.turnover.actual / 1000000).toFixed(1)}
                      M / €{companyThresholds.turnover.threshold / 1000000}M
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span>Active totale:</span>
                    <span
                      className={
                        companyThresholds.assets.actual >=
                          companyThresholds.assets.threshold
                          ? "text-destructive font-medium"
                          : "text-muted-foreground"
                      }
                    >
                      €{(companyThresholds.assets.actual / 1000000).toFixed(1)}M
                      / €{companyThresholds.assets.threshold / 1000000}M
                    </span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Services Analysis */}
              <div className="space-y-2">
                <h4 className="text-sm font-heading font-semibold flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  Analiza Serviciilor
                </h4>
                <div className="space-y-1 ml-4">
                  <div className="text-xs">
                    <span className="text-muted-foreground">
                      CAEN principal:
                    </span>
                    <span className="ml-1 font-medium">
                      {entityData.caen_principal.cod} -{" "}
                      {entityData.caen_principal.denumire}
                    </span>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground">
                      Servicii alese:
                    </span>
                    <span className="ml-1 font-medium">
                      {selectedServices.length} servicii
                    </span>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground">
                      Mapare CAEN→NIS2:
                    </span>
                    <span className="ml-1 font-medium text-primary">
                      Identificate în sistemul de clasificare
                    </span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Special Lists Analysis */}
              <div className="space-y-2">
                <h4 className="text-sm font-heading font-semibold flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Liste Speciale:{" "}
                  {hasListeSpeciale
                    ? "Potrivire(i) identificată(e)"
                    : "Nicio potrivire identificată"}
                </h4>
                <div className="space-y-1 ml-4">
                  {hasListeSpeciale ? (
                    <>
                      <p className="text-xs text-muted-foreground">
                        Compania dumneavoastră a fost identificată în
                        următoarele liste speciale, ceea ce reprezintă un
                        criteriu direct sau un indicator puternic pentru
                        încadrarea în Directiva NIS2:
                      </p>
                      {dataWithListaSpeciala.map((service) => {
                        return service.lista_details.map((detail) => (
                          <p
                            key={detail.url}
                            className="text-xs text-muted-foreground"
                          >
                            - {detail.url_name}
                          </p>
                        ));
                      })}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Compania dumneavoastră nu a fost găsită în niciuna dintre
                      listele speciale monitorizate. <br /> <br />
                      Prin urmare, rezultatul final al evaluării s-a bazat pe
                      analiza coroborată a următoarelor date: codul CAEN,
                      dimensiunea entității (număr de angajați, cifră de
                      afaceri, total active) și serviciile pe care le-ați
                      confirmat în cadrul aplicației.
                    </p>
                  )}
                </div>
              </div>

              <Separator />
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  };

  const handleNavigateToImpactAnalysis = () => {
    navigate("/analiza-efect-perturbator");
  };

  const handleContinue = () => {
    if (hasInsufficientCredits) {
      setShowPaywall(true);
      return;
    }
    // Check if impact analysis is required but not completed
    if (
      classification!.classificationV2 === RESULT_ENUM.IMPACT &&
      !entityData.impact_analysis
    ) {
      toast({
        title: "Analiza de impact necesară",
        description:
          "Vă rugăm să completați analiza de impact pe pagina dedicată înainte de a genera raportul.",
        variant: "destructive",
      });
      return;
    }

    onNext(classification!);
  };

  if (isEvaluating) {
    return (
      <EvaluationLoadingScreen
        currentStep={currentStep}
        steps={steps}
        onBack={onBack}
        onStepClick={onStepClick}
        userAvatar={userAvatar}
      />
    );
  }

  if (!classification) {
    return (
      <WizardLayout
        title="Încărcare..."
        description="Se pregătește clasificarea"
        currentStep={currentStep}
        steps={steps}
        onBack={onBack}
        onStepClick={onStepClick}
        showBackButton
        userAvatar={userAvatar}
      >
        <div className="text-center">Încărcare date...</div>
      </WizardLayout>
    );
  }

  return (
    <WizardLayout
      title="Rezultat Clasificare NIS 2"
      description="Algoritmul DNSC a fost aplicat cu succes. Vedeți rezultatul clasificării mai jos."
      currentStep={currentStep}
      steps={steps}
      onBack={onBack}
      onStepClick={onStepClick}
      showBackButton
      userAvatar={userAvatar}
      footer={
        <div className="ml-auto flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onBack}
            className="min-h-[44px]"
          >
            Înapoi
          </Button>
          <PaywallButton
            locked={hasInsufficientCredits}
            onClick={handleContinue}
            className="min-h-[44px]"
          >
            Generează Raport
          </PaywallButton>
        </div>
      }
    >
      <div className="max-w-6xl mx-auto space-y-4 md:space-y-6">
        {/* Services Analysis */}

        {/* Classification Result */}
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="md:text-2xl text-lg flex items-center gap-3">
              {getClassificationIcon(classification.classificationV2)}
              Rezultatul evaluării conform NIS2
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0 pb-4 md:pt-0 md:pb-6">
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <ClassificationBadge
                  classification={
                    classification.classificationV2 as ClassificationType
                  }
                  className="text-sm px-2 py-1 md:text-lg md:px-4 md:py-2"
                />
                {classification.needsImpactAnalysis && (
                  <Badge
                    variant="outline"
                    className="border-warning text-warning text-sm px-2 py-1 md:text-lg md:px-4 md:py-2"
                  >
                    Necesită Analiză de Impact
                  </Badge>
                )}
              </div>

              {classification.classificationV2 === RESULT_ENUM.IMPACT && (
                <Button
                  variant="outline"
                  onClick={handleNavigateToImpactAnalysis}
                  className="border-warning text-warning hover:bg-warning/10 w-fit"
                >
                  Completeaza analiza privind efectul perturbator
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Impact Analysis Results */}
        {entityData.impact_analysis && (
          <>
            <ClassificationReferenceTable />
            <ImpactResultsTable
              impactData={entityData.impact_analysis}
              showEditButton={true}
            />
          </>
        )}

        {/* Three Pillars Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="md:text-2xl text-lg">
              Analiza Celor Trei Piloni Decizionali
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              Algoritmul se bazează pe trei piloni interconectați conform OUG
              nr. 155/2024
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 pb-4 md:pt-0 md:pb-6">
            {renderPillarAnalysis(classification.sectorGroups)}
          </CardContent>
        </Card>

        {/* Services Analysis - moved after Three Pillars */}
        {renderServicesAnalysis()}

        {/* Detailed Conditions Analysis */}
        {renderDetailedConditionsAnalysis(classification.conditions)}

        {/* Next Steps */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base md:text-2xl">
              Pași Următori
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-6 md:pt-0 md:pb-6">
            {classification.classificationV2 === RESULT_ENUM.IMPACT ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-warning">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="font-medium">
                    Este necesară o analiză de impact
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Entitatea se află într-o zonă gri a clasificării. Pentru o
                  determinare precisă, va fi realizată o analiză privind efectul
                  perturbator cu ajutorul inteligenței artificiale pe o pagină
                  dedicată.
                </p>
                <Button
                  variant="outline"
                  onClick={handleNavigateToImpactAnalysis}
                  className="border-warning text-warning hover:bg-warning/10 w-fit"
                >
                  Accesează pagina de Analiză de Impact
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-success">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">Clasificarea este finală</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Puteți continua pentru a genera raportul oficial DNSC.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

      </div>
      <PaywallDialog
        open={showPaywall}
        onOpenChange={setShowPaywall}
        actionType={CREDIT_ACTION_TYPES.ANALIZA_EFECT_PERTURBATOR}
      />
    </WizardLayout>
  );
}
